import { useTranslate } from '@/translations';
import { AppSearch } from '@/types';
import { StorageKeys } from '@/utils/constants';
import { Button, Dropdown, Form, Input, MenuProps, Modal, Tag } from 'antd';
import React, { useCallback, useEffect, useMemo, useState, memo } from 'react';
import { ColumnFilterItem } from 'antd/es/table/interface';
import { useTheme } from 'styled-components';
import dayjs from 'dayjs';
import { faAdd, faClose, faEdit, faEllipsis, faTrash, Icon } from '../Icon';
import { RenderType } from './types';

const { confirm: confirmModal } = Modal;

export interface QuickViewColumns<T> {
  key: keyof T;
  label: string;
  filterOptions?: ColumnFilterItem[];
  renderType?: RenderType;
}

interface QuickViewPanelProps<T> {
  columns: QuickViewColumns<T>[];
  appSearchStorage: StorageKeys;
  defaultAppSearch?: AppSearch<T>;
  appSearch: AppSearch<T>;
  changeAppSearch: (query: Partial<AppSearch<T>>) => void;
}
function QuickViewPanelInner<T>({
  columns,
  appSearchStorage,
  defaultAppSearch,
  appSearch,
  changeAppSearch,
}: QuickViewPanelProps<T>) {
  const [quickViews, setQuickViews] = useState<string[]>([]);
  const [showSaveQuickView, setShowSaveQuickView] = useState(false);
  const [form] = Form.useForm();
  const { t } = useTranslate();
  const { lightGreen } = useTheme();

  const dateFieldKeys = useMemo(() => {
    return columns
      .filter((c) => c.renderType === 'date')
      .map((c) => String(c.key));
  }, [columns]);

  const normalizeQuickViewQuery = useCallback(
    (query: AppSearch<T>) => {
      // QuickView values are persisted via localStorage (JSON.stringify),
      // so Date objects turn into strings. Convert date filter values back.
      query.filters = query.filters?.map((filter) => {
        if (!dateFieldKeys.includes(String(filter.key))) return filter;
        if (!Array.isArray(filter.value)) return filter;

        const normalized = filter.value
          .map((v: any) => (v instanceof Date ? v : new Date(v)))
          .filter((d: Date) => !Number.isNaN(d.getTime()));

        return { ...filter, value: normalized };
      });

      return query;
    },
    [dateFieldKeys],
  );

  const loadStorageQuickViews = useCallback(() => {
    const prefix = `${appSearchStorage}-`;
    const views = Object.keys(window.localStorage)
      .filter(
        (key) => key.startsWith(prefix) && key !== `${appSearchStorage}-view`,
      )
      .map((key) => key.replace(prefix, ''));
    setQuickViews(views);
  }, [appSearchStorage]);

  useEffect(() => {
    loadStorageQuickViews();
  }, [loadStorageQuickViews]);

  const onDeleteQuickView = useCallback(
    (key: string) => {
      confirmModal({
        title: 'Confirm',
        content: 'Do you want to delete the quick view?',
        onOk() {
          window.localStorage.removeItem(`${appSearchStorage}-${key}`);
          loadStorageQuickViews();
        },
      });
    },
    [appSearchStorage, loadStorageQuickViews],
  );

  const quickViewMenuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = [];

    items.push(
      ...quickViews.map((o) => ({
        key: o,
        label: (
          <div className="w-100 d-flex align-items-center gap-2">
            <span className="flex-fill">{o}</span>
            {appSearch.name !== o ? (
              <Icon
                icon={faTrash}
                className="flex-1"
                onClick={(e: any) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onDeleteQuickView(o);
                }}
              />
            ) : null}
          </div>
        ),
      })),
    );

    const shouldShowManage = !!appSearch.filters || !!appSearch.sortField;
    if (items.length > 0 && shouldShowManage) {
      items.push({ type: 'divider' });
    }
    if (shouldShowManage) {
      items.push({
        key: 'manage-add',
        label: 'Add as new quick view',
        icon: <Icon icon={faAdd} />,
      });
      if (appSearch.name) {
        items.push({
          key: 'manage-update',
          label: 'Update current quick view',
          icon: <Icon icon={faEdit} />,
        });
      }
    }
    return items;
  }, [
    appSearch.filters,
    appSearch.name,
    appSearch.sortField,
    onDeleteQuickView,
    quickViews,
  ]);

  const onMenuClick = useCallback(
    (menuKey: string) => {
      if (menuKey === 'manage-add') {
        form.resetFields();
        setShowSaveQuickView(true);
      } else if (menuKey === 'manage-update') {
        window.localStorage.setItem(
          `${appSearchStorage}-${appSearch.name}`,
          JSON.stringify(appSearch),
        );
        loadStorageQuickViews();
      } else if (menuKey === 'manage-clear') {
        changeAppSearch(
          defaultAppSearch
            ? { ...defaultAppSearch }
            : {
                name: undefined,
                search: undefined,
                sortField: undefined,
                sortOrder: undefined,
                filters: undefined,
              },
        );
      } else {
        const query = JSON.parse(
          window.localStorage.getItem(`${appSearchStorage}-${menuKey}`) || '{}',
        ) as AppSearch<T>;
        changeAppSearch(normalizeQuickViewQuery(query));
      }
    },
    [
      appSearch,
      appSearchStorage,
      changeAppSearch,
      defaultAppSearch,
      form,
      loadStorageQuickViews,
      normalizeQuickViewQuery,
    ],
  );

  const onAddQuickView = useCallback(
    (values: any) => {
      if (quickViews.includes(values.name)) {
        form.setFields([{ name: 'name', errors: ['The name already exist.'] }]);
        return;
      }
      appSearch.name = values.name;
      window.localStorage.setItem(
        `${appSearchStorage}-${values.name}`,
        JSON.stringify(appSearch),
      );
      loadStorageQuickViews();
      setShowSaveQuickView(false);
    },
    [appSearch, appSearchStorage, form, loadStorageQuickViews, quickViews],
  );

  const onCloseOneFilter = useCallback(
    (key: keyof T) => {
      const filters = appSearch.filters?.filter((f) => f.key !== key);
      changeAppSearch({ filters });
    },
    [appSearch.filters, changeAppSearch],
  );

  const showFilters = useMemo(() => {
    if (!appSearch.filters?.length) return [];
    return appSearch.filters
      .map((filter) => {
        const valueLength = filter.value.length;
        if (valueLength < 1) return null;
        const column = columns.find((o) => o.key === filter.key);
        if (!column) return null;

        const operators = ['Contains', 'Equal', 'Not', '<=', '>='];
        const operatorIndex = filter.value.findIndex((o) =>
          operators.includes(o),
        );
        const filterOperator =
          operatorIndex === -1
            ? column.renderType === 'date'
              ? 'Between'
              : 'Contains'
            : filter.value[operatorIndex];
        const filterValues = filter.value.filter(
          (_, idx) => idx !== operatorIndex,
        );
        const showValues = column.filterOptions
          ? filterValues.map(
              (o) =>
                column.filterOptions?.find(
                  (f) => f.value == o || f.value.toString() === o,
                )?.text,
            )
          : column.renderType === 'date'
            ? filterValues.map((o) => dayjs(o).format('YYYY-MM-DD'))
            : filterValues;

        return {
          key: filter.key,
          label: column.label,
          opertate: filterOperator,
          value: showValues.join(', '),
        };
      })
      .filter((o) => o)
      .map((o) => o!);
  }, [appSearch.filters, columns]);

  const quickViewDropdownMenu = useMemo(
    () => ({
      items: quickViewMenuItems,
      onClick: (e: { key: string }) => onMenuClick(e.key),
    }),
    [quickViewMenuItems, onMenuClick],
  );

  return (
    <>
      <div className="w-100 min-w-0 d-flex align-items-center flex-wrap gap-2">
        <div>
          <b className="me-2">{t('quickView')}:</b>
          <Dropdown menu={quickViewDropdownMenu}>
            <Button size="large" style={{ minWidth: 150 }}>
              <div className="w-100 d-flex align-items-center gap-2">
                <span
                  className={`text-start flex-fill${
                    appSearch?.name ? '' : ' text-muted'
                  }`}
                >
                  {appSearch?.name ? appSearch?.name : t('select')}
                </span>
                <Icon icon={faEllipsis} className="flex-1" />
              </div>
            </Button>
          </Dropdown>
        </div>
        {showFilters.length ? (
          <>
            {showFilters.map((filter) => (
              <Tag
                key={filter.key as string}
                closeIcon
                onClose={() => onCloseOneFilter(filter.key)}
                className="py-1 m-0 text-wrap"
              >
                <b>{filter.label}</b>
                <span
                  className="mx-1 p-1 rounded-1"
                  style={{ background: lightGreen }}
                >
                  {filter.opertate}
                </span>
                {filter.value}
              </Tag>
            ))}
            {showFilters.length > 1 ? (
              <Button onClick={() => onMenuClick('manage-clear')}>
                <Icon icon={faClose} />
                Clear all filters
              </Button>
            ) : null}
          </>
        ) : null}
      </div>

      <Modal
        open={showSaveQuickView}
        title={t('saveQuickView')}
        onCancel={() => setShowSaveQuickView(false)}
        maskClosable
        okText={t('save')}
        onOk={() => form.submit()}
        footer={(_, { OkBtn }) => <OkBtn />}
      >
        <Form id="quick-view-form" form={form} onFinish={onAddQuickView}>
          <Form.Item
            label={t('quickViewName')}
            name="name"
            rules={[{ required: true }]}
          >
            <Input autoComplete="off" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export const QuickViewPanel = memo(
  QuickViewPanelInner,
) as typeof QuickViewPanelInner;
